import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 12,
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 30,
    textAlign: 'center',
    borderBottom: '2 solid #1f2937',
    paddingBottom: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#1f2937',
  },
  subtitle: {
    fontSize: 16,
    color: '#6b7280',
    marginBottom: 10,
  },
  generatedDate: {
    fontSize: 10,
    color: '#9ca3af',
  },
  section: {
    marginBottom: 25,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 15,
    color: '#1f2937',
    borderBottom: '1 solid #e5e7eb',
    paddingBottom: 8,
  },
  subsectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#374151',
    marginTop: 15,
  },
  grid: {
    flexDirection: 'row',
    marginBottom: 15,
  },
  gridItem: {
    flex: 1,
    marginRight: 20,
  },
  label: {
    fontSize: 10,
    color: '#6b7280',
    marginBottom: 3,
  },
  value: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  currencyValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#059669',
  },
  table: {
    marginTop: 10,
    border: '1 solid #e5e7eb',
  },
  tableHeader: {
    backgroundColor: '#f9fafb',
    flexDirection: 'row',
    borderBottom: '1 solid #e5e7eb',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #e5e7eb',
  },
  tableCell: {
    flex: 1,
    padding: 8,
    fontSize: 10,
  },
  tableCellHeader: {
    fontWeight: 'bold',
    backgroundColor: '#f9fafb',
  },
  tableCellWide: {
    flex: 2,
    padding: 8,
    fontSize: 10,
  },
  tableCellNarrow: {
    flex: 0.5,
    padding: 8,
    fontSize: 10,
  },
  summaryBox: {
    backgroundColor: '#f0f9ff',
    padding: 20,
    borderRadius: 8,
    marginTop: 20,
    marginBottom: 20,
  },
  summaryTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1e40af',
    marginBottom: 15,
  },
  summaryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  summaryItem: {
    width: '33.33%',
    marginBottom: 15,
  },
  chartContainer: {
    marginTop: 15,
    marginBottom: 15,
  },
  chartTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#374151',
  },
  disclaimer: {
    fontSize: 9,
    color: '#9ca3af',
    textAlign: 'center',
    marginTop: 30,
    paddingTop: 20,
    borderTop: '1 solid #e5e7eb',
  },
});

interface TeamRole {
  id: string;
  name: string;
  salary: number;
  hourlyRate?: number; // optional hourly rate in USD, used to auto-calculate salary
  headcount: number[];
  category: string;
}

interface OperatingCost {
  id: string;
  name: string;
  monthlyCosts: number[];
  category: string;
}

interface InvestorReportPDFProps {
  projectName: string;
  currency: string;
  investmentData: {
    investmentAmount: number;
    equityPercentage: number;
    soloPrice: number;
    teamPrice: number;
    unlimitedPrice: number;
    monthlyOpex: number;
    initialUsers: number;
    monthlyGrowthRate: number;
    revenueMultiple: number;
  };
  teamRoles: TeamRole[];
  operatingCosts: OperatingCost[];
  projectionData: any[];
  breakEvenMonth: number | null;
  finalMetrics: any;
  companyValuation: number;
  investorEquityValue: number;
  roi: number;
  formatCurrency: (value: number) => string;
  chartImages?: {
    cashChart?: string;
    revenueChart?: string;
    expensesChart?: string;
    usersChart?: string;
  };
}

export const InvestorReportPDF: React.FC<InvestorReportPDFProps> = ({
  projectName,
  currency,
  investmentData,
  teamRoles,
  operatingCosts,
  projectionData,
  breakEvenMonth,
  finalMetrics,
  companyValuation,
  investorEquityValue,
  roi,
  formatCurrency,
  chartImages,
}) => {
  const formatNumber = (value: number) => value.toLocaleString();

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>{projectName}</Text>
          <Text style={styles.subtitle}>Comprehensive Investor Report</Text>
          <Text style={styles.generatedDate}>Generated on: {new Date().toLocaleDateString()}</Text>
        </View>

        {/* Executive Summary */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Executive Summary</Text>
          <View style={styles.summaryBox}>
            <Text style={styles.summaryTitle}>Key Investment Highlights</Text>
            <View style={styles.summaryGrid}>
              <View style={styles.summaryItem}>
                <Text style={styles.label}>Investment Amount</Text>
                <Text style={styles.currencyValue}>{formatCurrency(investmentData.investmentAmount)}</Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.label}>Equity Stake</Text>
                <Text style={styles.value}>{investmentData.equityPercentage}%</Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.label}>3-Year ROI</Text>
                <Text style={styles.value}>{roi.toFixed(1)}%</Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.label}>Break-Even</Text>
                <Text style={styles.value}>{breakEvenMonth ? `Month ${breakEvenMonth}` : 'Not reached'}</Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.label}>Company Valuation</Text>
                <Text style={styles.currencyValue}>{formatCurrency(companyValuation)}</Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.label}>Investor Returns</Text>
                <Text style={styles.currencyValue}>{formatCurrency(investorEquityValue)}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Investment Overview */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Investment Overview</Text>
          <View style={styles.grid}>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Investment Amount</Text>
              <Text style={styles.currencyValue}>{formatCurrency(investmentData.investmentAmount)}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Equity Percentage</Text>
              <Text style={styles.value}>{investmentData.equityPercentage}%</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Revenue Multiple</Text>
              <Text style={styles.value}>{investmentData.revenueMultiple}x</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Monthly OPEX</Text>
              <Text style={styles.currencyValue}>{formatCurrency(investmentData.monthlyOpex)}</Text>
            </View>
          </View>
        </View>

        {/* Pricing Strategy */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Pricing Strategy</Text>
          <View style={styles.grid}>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Solo Plan (Monthly)</Text>
              <Text style={styles.currencyValue}>{formatCurrency(investmentData.soloPrice)}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Team Plan (5 Users)</Text>
              <Text style={styles.currencyValue}>{formatCurrency(investmentData.teamPrice)}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Unlimited Plan</Text>
              <Text style={styles.currencyValue}>{formatCurrency(investmentData.unlimitedPrice)}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Initial Users</Text>
              <Text style={styles.value}>{formatNumber(investmentData.initialUsers)}</Text>
            </View>
          </View>
        </View>

        {/* Growth Projections */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Growth Projections</Text>
          <View style={styles.grid}>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Monthly Growth Rate</Text>
              <Text style={styles.value}>{investmentData.monthlyGrowthRate}%</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Year 1 Users</Text>
              <Text style={styles.value}>{formatNumber(projectionData[11]?.users || 0)}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Year 2 Users</Text>
              <Text style={styles.value}>{formatNumber(projectionData[23]?.users || 0)}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Year 3 Users</Text>
              <Text style={styles.value}>{formatNumber(finalMetrics.users)}</Text>
            </View>
          </View>
        </View>

        {/* Financial Projections */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Financial Projections</Text>

          <Text style={styles.subsectionTitle}>Revenue vs Expenses (Key Milestones)</Text>
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableCell, styles.tableCellHeader]}>Period</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader]}>Revenue</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader]}>Expenses</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader]}>Net Cash Flow</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader]}>Cumulative Cash</Text>
            </View>
            {[5, 11, 17, 23, 29, 35].map((monthIndex) => {
              const data = projectionData[monthIndex];
              if (!data) return null;
              return (
                <View key={monthIndex} style={styles.tableRow}>
                  <Text style={styles.tableCell}>Month {monthIndex + 1}</Text>
                  <Text style={styles.tableCell}>{formatCurrency(data.revenue)}</Text>
                  <Text style={styles.tableCell}>{formatCurrency(data.expenses)}</Text>
                  <Text style={styles.tableCell}>{formatCurrency(data.netCashFlow)}</Text>
                  <Text style={styles.tableCell}>{formatCurrency(data.cumulativeCash)}</Text>
                </View>
              );
            })}
          </View>
        </View>

        {/* Team & Manpower */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Team & Manpower</Text>

          <Text style={styles.subsectionTitle}>Team Composition & Salaries</Text>
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableCellWide, styles.tableCellHeader]}>Role</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader]}>Monthly Salary</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader]}>Category</Text>
              <Text style={[styles.tableCellNarrow, styles.tableCellHeader]}>M6</Text>
              <Text style={[styles.tableCellNarrow, styles.tableCellHeader]}>M12</Text>
              <Text style={[styles.tableCellNarrow, styles.tableCellHeader]}>M18</Text>
              <Text style={[styles.tableCellNarrow, styles.tableCellHeader]}>M24</Text>
            </View>
            {teamRoles.map((role) => (
              <View key={role.id} style={styles.tableRow}>
                <Text style={styles.tableCellWide}>{role.name}</Text>
                <Text style={styles.tableCell}>{formatCurrency(role.salary)}</Text>
                <Text style={styles.tableCell}>{role.category}</Text>
                <Text style={styles.tableCellNarrow}>{role.headcount[5] || 0}</Text>
                <Text style={styles.tableCellNarrow}>{role.headcount[11] || 0}</Text>
                <Text style={styles.tableCellNarrow}>{role.headcount[17] || 0}</Text>
                <Text style={styles.tableCellNarrow}>{role.headcount[23] || 0}</Text>
              </View>
            ))}
          </View>

          <Text style={styles.subsectionTitle}>Headcount Summary</Text>
          <View style={styles.grid}>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Month 6 Total Headcount</Text>
              <Text style={styles.value}>
                {teamRoles.reduce((sum, role) => sum + (role.headcount[5] || 0), 0)}
              </Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Month 12 Total Headcount</Text>
              <Text style={styles.value}>
                {teamRoles.reduce((sum, role) => sum + (role.headcount[11] || 0), 0)}
              </Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Month 18 Total Headcount</Text>
              <Text style={styles.value}>
                {teamRoles.reduce((sum, role) => sum + (role.headcount[17] || 0), 0)}
              </Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Month 24 Total Headcount</Text>
              <Text style={styles.value}>
                {teamRoles.reduce((sum, role) => sum + (role.headcount[23] || 0), 0)}
              </Text>
            </View>
          </View>
        </View>

        {/* Operating Costs */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Operating Costs</Text>
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableCellWide, styles.tableCellHeader]}>Category</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader]}>Type</Text>
              <Text style={[styles.tableCellNarrow, styles.tableCellHeader]}>M6</Text>
              <Text style={[styles.tableCellNarrow, styles.tableCellHeader]}>M12</Text>
              <Text style={[styles.tableCellNarrow, styles.tableCellHeader]}>M18</Text>
              <Text style={[styles.tableCellNarrow, styles.tableCellHeader]}>M24</Text>
            </View>
            {operatingCosts.map((cost) => (
              <View key={cost.id} style={styles.tableRow}>
                <Text style={styles.tableCellWide}>{cost.name}</Text>
                <Text style={styles.tableCell}>{cost.category}</Text>
                <Text style={styles.tableCellNarrow}>
                  {formatCurrency(cost.monthlyCosts[5] || 0)}
                </Text>
                <Text style={styles.tableCellNarrow}>
                  {formatCurrency(cost.monthlyCosts[11] || 0)}
                </Text>
                <Text style={styles.tableCellNarrow}>
                  {formatCurrency(cost.monthlyCosts[17] || 0)}
                </Text>
                <Text style={styles.tableCellNarrow}>
                  {formatCurrency(cost.monthlyCosts[23] || 0)}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* Charts */}
        {chartImages && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Financial Charts & Visualizations</Text>

            {chartImages.cashChart && (
              <View style={styles.chartContainer}>
                <Text style={styles.chartTitle}>Cumulative Cash Position (36 Months)</Text>
                <Image src={chartImages.cashChart} style={{ width: '100%', height: 180 }} />
              </View>
            )}

            {chartImages.revenueChart && (
              <View style={styles.chartContainer}>
                <Text style={styles.chartTitle}>Revenue by Pricing Tier (36 Months)</Text>
                <Image src={chartImages.revenueChart} style={{ width: '100%', height: 180 }} />
              </View>
            )}

            {chartImages.expensesChart && (
              <View style={styles.chartContainer}>
                <Text style={styles.chartTitle}>Monthly Revenue vs Expenses (36 Months)</Text>
                <Image src={chartImages.expensesChart} style={{ width: '100%', height: 180 }} />
              </View>
            )}

            {chartImages.usersChart && (
              <View style={styles.chartContainer}>
                <Text style={styles.chartTitle}>User Growth Projection (36 Months)</Text>
                <Image src={chartImages.usersChart} style={{ width: '100%', height: 180 }} />
              </View>
            )}
          </View>
        )}

        {/* 3-Year Summary */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>3-Year Financial Summary</Text>
          <View style={styles.summaryBox}>
            <View style={styles.summaryGrid}>
              <View style={styles.summaryItem}>
                <Text style={styles.label}>Total Revenue</Text>
                <Text style={styles.currencyValue}>{formatCurrency(finalMetrics.totalRevenue)}</Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.label}>Total Expenses</Text>
                <Text style={styles.currencyValue}>{formatCurrency(investmentData.monthlyOpex * 36)}</Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.label}>Net Profit</Text>
                <Text style={styles.currencyValue}>
                  {formatCurrency(finalMetrics.totalRevenue - investmentData.monthlyOpex * 36)}
                </Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.label}>Company Valuation</Text>
                <Text style={styles.currencyValue}>{formatCurrency(companyValuation)}</Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.label}>Investor Equity Value</Text>
                <Text style={styles.currencyValue}>{formatCurrency(investorEquityValue)}</Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.label}>ROI Multiple</Text>
                <Text style={styles.value}>
                  {((investorEquityValue / investmentData.investmentAmount) - 1).toFixed(2)}x
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Disclaimer */}
        <View style={styles.disclaimer}>
          <Text>
            This investor report is for informational purposes only and does not constitute financial advice.
            All projections are based on the assumptions provided and actual results may vary significantly.
            Past performance does not guarantee future results. Please consult with qualified financial advisors
            before making any investment decisions.
          </Text>
        </View>
      </Page>
    </Document>
  );
};